import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  Plus,
  RefreshCw,
  Search,
  X,
  Sparkles,
  Layers,
  Sliders,
} from "lucide-react";
import {
  guardrailsListItems,
  guardrailsCreateItem,
  guardrailsSeedTaxonomies,
} from "../gen";
import type { GuardrailItemResponse } from "../gen/models";
import { useOrg } from "../hooks/useOrg";
import { useSearchQuery } from "../hooks/useSearchQuery";
import { useFilterParam } from "../hooks/useFilterParam";
import { useToast } from "../context/ToastContext";
import { GuardrailDetail } from "./GuardrailDetail";
import { CreateGuardrailModal } from "../components/guardrails/CreateGuardrailModal";
import { PaginationControls } from "../components/common/PaginationControls";
import { ScrollableColumnContainer } from "../components/common/ScrollableColumnContainer";
import { FilterSelect, type FilterSelectOption } from "../components/common/FilterSelect";
import { useAgentOptions } from "../hooks/useAgentOptions";
import { TimeAgo } from "../components/common/TimeAgo";
import { normalizeAgentType } from "../hooks/useUserDirectory";

const GUARDRAIL_STATUS_OPTIONS: FilterSelectOption[] = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "archived", label: "Archived" },
];

const GUARDRAIL_ACTION_OPTIONS: FilterSelectOption[] = [
  { value: "block", label: "Block" },
  { value: "flag", label: "Flag" },
];

const GUARDRAIL_CATEGORY_OPTIONS: FilterSelectOption[] = [
  { value: "prompt_injection", label: "Prompt Injection" },
  { value: "jailbreak", label: "Jailbreak" },
  { value: "off_topic", label: "Off-Topic" },
  { value: "abusive", label: "Abusive / Harass" },
  { value: "brand_safety", label: "Brand Safety" },
  { value: "pii_leakage", label: "PII & Secrets" },
  { value: "boundary", label: "Boundary" },
];

export function GuardrailsWorkspace() {
  const { item_id } = useParams<{ item_id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { activeOrgId } = useOrg();

  const [items, setItems] = useState<GuardrailItemResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [agentFilter, setAgentFilter] = useFilterParam("agent", "all", ["agent_type"]);
  const [statusFilter, setStatusFilter] = useFilterParam("status", "all");
  const [actionFilter, setActionFilter] = useFilterParam("action", "all");
  const [categoryFilter, setCategoryFilter] = useFilterParam("category", "all");
  const [searchQuery, setSearchQuery] = useSearchQuery("q");

  const agentOptions = useAgentOptions({ includeUniversal: true });

  // Create Modal state
  const [isCreating, setIsCreating] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      if (agentFilter !== "all") params.agent_type = agentFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (actionFilter !== "all") params.action = actionFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (searchQuery.trim()) params.q = searchQuery.trim();

      const res = await guardrailsListItems(params);
      const data = (res as any).data;
      if (data && Array.isArray(data.items)) {
        setItems(data.items);
        setTotal(data.total ?? data.items.length);
      } else if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems([]);
        setTotal(0);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load guardrails");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [agentFilter, statusFilter, actionFilter, categoryFilter, searchQuery]);

  useEffect(() => {
    loadItems();
  }, [agentFilter, statusFilter, actionFilter, categoryFilter, searchQuery, page, pageSize, activeOrgId]);

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const res = await guardrailsSeedTaxonomies();
      const count = (res as any).data?.created_count ?? 0;
      toast.success(`Successfully seeded ${count} default safety guardrails.`);
      await loadItems();
    } catch (err: any) {
      toast.error("Failed to seed guardrails: " + (err.message || String(err)));
    } finally {
      setSeeding(false);
    }
  };

  const handleCreateGuardrail = async (formData: {
    title: string;
    description: string;
    category: string;
    agent_type?: string;
    action: string;
    severity: string;
    default_threshold: number;
    on_block_action: string;
    canned_response_text: string;
    advisory: string;
    anchors: Array<{ anchor_type: string; text: string }>;
  }) => {
    setCreateSubmitting(true);
    try {
      const metadata: Record<string, any> = {};
      if (formData.advisory.trim()) {
        metadata.advisory = formData.advisory.trim();
      }
      if (formData.action === "block") {
        metadata.on_block_action = formData.on_block_action;
        if (formData.on_block_action === "canned_response" && formData.canned_response_text.trim()) {
          metadata.canned_response_text = formData.canned_response_text.trim();
        }
      }

      const res = await guardrailsCreateItem({
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        category: formData.category,
        agent_type: formData.agent_type === "universal" ? undefined : formData.agent_type,
        action: formData.action as any,
        severity: formData.severity as any,
        default_threshold: formData.default_threshold,
        status: "active",
        metadata,
        anchors: formData.anchors.length > 0 ? formData.anchors : undefined,
      });

      const created = (res as any).data as GuardrailItemResponse;
      setIsCreating(false);
      toast.success("Safety guardrail registered and vector anchors indexed.");
      navigate(`/guardrails/${created.id}${location.search}`);
      loadItems();
    } catch (err: any) {
      toast.error("Failed to create guardrail: " + (err.message || String(err)));
    } finally {
      setCreateSubmitting(false);
    }
  };

  const filteredItems = items;
  const hasSelectedItem = Boolean(item_id && filteredItems.some((it) => it.id === item_id));
  const hasSelection = Boolean(item_id);

  return (
    <div className="flex h-full w-full overflow-hidden min-h-0">
      {/* ============================================================== */}
      {/* COLUMN 2: Compact Guardrails List Pane                         */}
      {/* ============================================================== */}
      <div
        className={`${
          hasSelection ? "hidden md:flex" : "flex"
        } w-full md:w-80 lg:w-[360px] xl:w-96 shrink-0 h-full border-r border-stone-200 dark:border-stone-800 bg-white/70 dark:bg-stone-900/60 flex-col min-h-0 overflow-hidden select-none transition-all`}
      >
        {/* Pane Header */}
        <div className="p-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between gap-2 shrink-0 bg-stone-50/50 dark:bg-stone-950/50">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-bold tracking-tight text-stone-900 dark:text-stone-100 truncate">
              Guardrails
            </h2>
            <span className="px-1.5 py-0.2 rounded-md bg-stone-200 dark:bg-stone-800 text-[10px] font-mono font-semibold text-stone-600 dark:text-stone-400">
              {filteredItems.length}
            </span>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={handleSeedDefaults}
              disabled={seeding || loading}
              className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
              title="Seed default safety taxonomies"
            >
              <Sparkles className={`w-3.5 h-3.5 ${seeding ? "animate-spin text-amber-500" : ""}`} />
            </button>
            <button
              type="button"
              onClick={loadItems}
              disabled={loading}
              className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
              title="Refresh guardrails list"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              onClick={() => setIsCreating(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>New</span>
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="p-2.5 border-b border-stone-100 dark:border-stone-800/80 shrink-0 space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 dark:text-stone-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search guardrails..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 border border-stone-200 dark:border-stone-800 focus:outline-hidden focus:ring-1 focus:ring-stone-400 dark:focus:ring-stone-600 font-sans shadow-2xs"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-1.5">
            <FilterSelect
              label="Agent"
              value={agentFilter}
              onChange={setAgentFilter}
              options={agentOptions}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={GUARDRAIL_STATUS_OPTIONS}
            />
          </div>

          <div className="grid grid-cols-2 gap-1.5">
            <FilterSelect
              label="Action"
              value={actionFilter}
              onChange={setActionFilter}
              options={GUARDRAIL_ACTION_OPTIONS}
            />
            <FilterSelect
              label="Category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={GUARDRAIL_CATEGORY_OPTIONS}
            />
          </div>
        </div>

        {/* Scrollable Compact List */}
        <ScrollableColumnContainer>
          {loading && items.length === 0 ? (
            <div className="p-8 text-center text-stone-400 dark:text-stone-500 text-xs flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-stone-500" />
              <span>Loading guardrails...</span>
            </div>
          ) : error ? (
            <div className="p-4 rounded-lg bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-400 text-xs">
              {error}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-8 text-center space-y-2">
              <Shield className="w-7 h-7 text-stone-300 dark:text-stone-600 mx-auto" />
              <div className="text-xs font-semibold text-stone-700 dark:text-stone-300">
                No guardrails configured
              </div>
              <p className="text-[11px] text-stone-400 dark:text-stone-500">
                Define negative intent anchors or seed default security taxonomies.
              </p>
              <button
                type="button"
                onClick={handleSeedDefaults}
                disabled={seeding}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 text-xs font-semibold hover:bg-stone-50 dark:hover:bg-stone-800 transition"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                <span>Seed Defaults</span>
              </button>
            </div>
          ) : (
            filteredItems.map((it) => {
              const isSelected = it.id === item_id;
              const isDimmed = hasSelectedItem && !isSelected;

              return (
                <div
                  key={it.id}
                  onClick={() => navigate(`/guardrails/${it.id}${location.search}`)}
                  className={`px-4 py-3 border-b border-l-2 text-xs cursor-pointer transition-all duration-150 space-y-1.5 ${
                    isSelected
                      ? "bg-white dark:bg-stone-900 border-l-stone-900 dark:border-l-stone-100 border-b-stone-200 dark:border-b-stone-800 shadow-2xs opacity-100"
                      : `border-l-transparent border-b-stone-200/80 dark:border-b-stone-800/60 hover:bg-stone-100/70 dark:hover:bg-stone-800/50 ${
                          isDimmed ? "opacity-50 hover:opacity-100" : "opacity-100"
                        }`
                  }`}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="px-1.5 py-0.2 rounded text-[10px] uppercase font-mono font-bold bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
                        {it.category.replace(/_/g, " ")}
                      </span>

                      <span
                        className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                          it.action === "block"
                            ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-400 dark:border-rose-900"
                            : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-900"
                        }`}
                      >
                        {it.action}
                      </span>
                    </div>

                    <span
                      className={`px-1.5 py-0.2 rounded-full text-[10px] font-semibold capitalize border ${
                        it.status === "active"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800"
                          : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700"
                      }`}
                    >
                      {it.status}
                    </span>
                  </div>

                  <div className="font-semibold text-[11px] text-stone-900 dark:text-stone-100 line-clamp-1 flex items-center gap-1">
                    {it.action === "block" ? (
                      <ShieldAlert className="w-3 h-3 text-rose-600 dark:text-rose-400 shrink-0" />
                    ) : (
                      <ShieldCheck className="w-3 h-3 text-amber-600 dark:text-amber-400 shrink-0" />
                    )}
                    <span className="truncate">{it.title}</span>
                  </div>

                  {it.description ? (
                    <p className="line-clamp-2 text-[11px] text-stone-500 dark:text-stone-400 font-sans">
                      {it.description}
                    </p>
                  ) : null}

                  <div className="flex items-center justify-between text-[10px] text-stone-400 dark:text-stone-500 pt-1 border-t border-stone-100 dark:border-stone-800/60 gap-1.5 font-mono">
                    <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                      <span className="inline-flex items-center gap-1 shrink-0">
                        <Layers className="w-2.5 h-2.5" />
                        <span>{it.anchor_count ?? 0} anchors</span>
                      </span>
                      <span>•</span>
                      <span className="text-stone-500 dark:text-stone-400">
                        ≥{it.default_threshold.toFixed(2)}
                      </span>
                      {it.agent_type && (
                        <>
                          <span>•</span>
                          <span className="text-stone-600 dark:text-stone-300 capitalize truncate">
                            {normalizeAgentType(it.agent_type)}
                          </span>
                        </>
                      )}
                    </div>
                    <TimeAgo date={it.created_at} className="shrink-0 font-mono" />
                  </div>
                </div>
              );
            })
          )}
        </ScrollableColumnContainer>

        {/* Pagination Controls */}
        <PaginationControls
          compact
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          disabled={loading}
        />
      </div>

      {/* ============================================================== */}
      {/* COLUMN 3: Detail Workspace Pane                                */}
      {/* ============================================================== */}
      <div
        className={`${
          hasSelection ? "flex" : "hidden md:flex"
        } flex-1 min-w-0 h-full overflow-y-auto [scrollbar-gutter:stable] flex-col bg-stone-50 dark:bg-stone-950 p-4 md:p-6`}
      >
        {item_id ? (
          <GuardrailDetail key={item_id} />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center select-none space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 flex items-center justify-center shadow-xs">
              <Shield className="w-6 h-6 text-stone-400 dark:text-stone-500" />
            </div>
            <div className="space-y-1 max-w-md">
              <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">
                Safety & Guardrail Manifold
              </h3>
              <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">
                Configure embedding guardrail anchors to intercept jailbreaks, prompt injections, and off-topic queries before agent execution. Select a guardrail from the list or create a custom safety barrier.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Create Guardrail</span>
              </button>
              <button
                type="button"
                onClick={handleSeedDefaults}
                disabled={seeding}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 text-xs font-semibold hover:bg-stone-100 dark:hover:bg-stone-800 transition shadow-2xs"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                <span>Seed Recommended Defaults</span>
              </button>
              <button
                type="button"
                onClick={() => navigate("/agents")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 text-xs font-semibold hover:bg-stone-100 dark:hover:bg-stone-800 transition shadow-2xs"
              >
                <Sliders className="w-3.5 h-3.5 text-stone-500" />
                <span>Agent Configs</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Guardrail Modal */}
      <CreateGuardrailModal
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        onSubmit={handleCreateGuardrail}
        submitting={createSubmitting}
      />
    </div>
  );
}
